import { Component, DestroyRef, ElementRef, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { v4 as uuid } from 'uuid';

import { Observable, Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { ItcModalConfig } from '@itc-core/directives/modal/common/modal.models';
import { LogoUploaderDialogComponent, UpdatePasswordDialogComponent, UserDetailsDialogComponent } from '../../dialog/components';
import {
	Constants,
	LOCAL_STORAGE_KEYS,
	LOGO_UPLOADER_TITLES,
	PROJECT_IMAGE_SIZES,
	SnackBarHelperComponent,
} from '../../helpers';
import { Company, ProjectSearch, ProjectTimeData, UserTime } from '../../interfaces';
import { UserModel } from '../../models';
import { LocalStorageService } from '../../services/local-storage.service';
import { ProjectTimeService } from '../../services/project-time.service';
import { S3Service } from '../../services/s3.service';
import { UserTimeService } from '../../services/user-time-service';
import { GetCurrentUserResolver, UserService } from '../../services/user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ImageService } from '../../services/image.service';

@Component({
	selector: 'app-user-profile',
	templateUrl: './user-profile.component.html',
	styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent implements OnInit {
	/**
	 * Router config resolvers
	 * @type {}
	 */
	public static routerResolvers = {
		user: GetCurrentUserResolver,
	};

	@ViewChild(LogoUploaderDialogComponent, { static: false }) public logoUploaderDialogComponent: LogoUploaderDialogComponent;
	@ViewChild('logoUploaderDialog', { static: true }) public logoUploaderDialogRef: ElementRef;
	@ViewChild(UserDetailsDialogComponent, { static: false }) public userDetailsDialogComponent: UserDetailsDialogComponent;
	@ViewChild('userDetailsDialog', { static: true }) public userDetailsDialogRef: ElementRef;
	@ViewChild(UpdatePasswordDialogComponent, { static: false }) public updatePasswordDialogComponent: UpdatePasswordDialogComponent;
	@ViewChild('updatePassword', { static: true }) public updatePasswordRef: TemplateRef<any>;

	public currentUser: UserModel;
	public displayedCompanies: Company[];
	public loadingSessions: Subscription;
	public logoUploaderTitles: typeof LOGO_UPLOADER_TITLES = LOGO_UPLOADER_TITLES;
	public paginateItemsPerPage = Constants.PAGINATE_ITEMS_5PER_PAGE;
	public projectTimesArray: ProjectTimeData[] = [];
	public projectTimesPageNo: number = 1;
	public projectTimesSearch: string;
	public sessions: UserTime[] = [];
	public showChangePasswordModal: boolean = false;
	public showUpdatePictureModal: boolean = false;
	public showUserDetailsModal: boolean = false;
	public totalUserTime: number;
	public updateCurrentUser: Observable<UserModel>;
	public updatePasswordModalConfig: ItcModalConfig = new ItcModalConfig({
		confirmButtonText: Constants.MODAL_TEXT.submit,
		title: Constants.MODAL_TITLE.changePassword,
		showCloseModalButton: true,
	});
	public updatePictureModalConfig: ItcModalConfig = new ItcModalConfig({
		confirmButtonText: Constants.MODAL_TEXT.submit,
		title: Constants.MODAL_TITLE.updatePicture,
		showCloseModalButton: true,
	});
	public uploadingFilePromise: Promise<any>;
	public userDetailsModalConfig: ItcModalConfig = new ItcModalConfig({
		confirmButtonText: Constants.MODAL_TEXT.submit,
		title: Constants.MODAL_TITLE.userDetails,
		showCloseModalButton: true,
	});
	public userPictureUrl = Constants.PROFILE_DEFAULT_PICTURE;

	private destroyRef = inject(DestroyRef);
	private searchProject: ProjectSearch = {
		name: '',
		estimators: [],
	};

	constructor(
		public snack: SnackBarHelperComponent,
		private localStorageService: LocalStorageService,
		private projectTimeService: ProjectTimeService,
		private route: ActivatedRoute,
		private s3Service: S3Service,
		private userService: UserService,
		private userTimeService: UserTimeService,
		private imageService: ImageService,
	) {}

	public ngOnInit(): void {
		this.currentUser = new UserModel();

		this.route.data.subscribe(({ user }) => {
			this.currentUser = user;
			if (this.currentUser.displayPictureKey) {
				this.s3Service
					.getSignedUrl(this.currentUser.displayPictureKey)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe(url => {
						this.userPictureUrl = url;
					});
			}
		});

		this.searchProject.estimators = [];
		this.searchProject.estimators.push(this.currentUser.id);
		// Only display first 3 companies
		this.displayedCompanies = this.currentUser ? this.currentUser.providers.filter((company: Company) => company.isActive === true).slice(0, 3) : [];

		this.getTimeEntries();
		this.getProjectTimesPage(1);
	}

	/**
	 * Closes the change password modal
	 */
	public resetPasswordModalForm(): void {
		this.updatePasswordDialogComponent.resetForm();
	}

	/**
	 * Closes the update picture modal
	 */
	public closeUpdatePictureModal(): void {
		this.showUpdatePictureModal = false;
	}

	/**
	 * Closes the user details modal
	 */
	public closeUserDetailsModal(): void {
		this.showUserDetailsModal = false;
	}

	/**
	 * Opens the Change Password Modal
	 */
	public openChangePasswordModal(): void {
		this.showChangePasswordModal = true;
	}

	/**
	 * Opens the Update Picture Modal
	 */
	public openUpdatePictureModal(): void {
		this.showUpdatePictureModal = true;
	}

	/**
	 * Opens the User Details Form Modal.
	 */
	public openUserDetailsModal(): void {
		this.showUserDetailsModal = true;
	}

	/**
	 * Submit change password modal and close modal on success
	 */
	public submitChangePasswordModal(): void {
		// TODO convert promises to observables
		this.updatePasswordDialogComponent.changePassword().then(() => {
			this.showChangePasswordModal = false;
		});
	}

	/**
	 * Submit update picture modal and close modal on success
	 */
	public submitUpdatePictureModal(): void {
		// TODO convert promises to observables
		this.logoUploaderDialogComponent.getImageFile().then((file: File | Blob) => {
			if (file !== undefined) {
				let downscaledFile = file;

				if (file instanceof File) {
					const blobObservable = this.imageService.downscaleImage(
						file,
						PROJECT_IMAGE_SIZES.thumbnail.width,
						PROJECT_IMAGE_SIZES.thumbnail.height,
						PROJECT_IMAGE_SIZES.thumbnail.quality,
					);

					blobObservable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(blob => {
						downscaledFile = new File([blob], file.name, { type: blob.type });
						this.uploadingFilePromise = this.uploadAndSaveImage(downscaledFile);
					});
				} else {
					this.uploadingFilePromise = this.uploadAndSaveImage(downscaledFile);
				}
			}
			// Close the modal
			this.showUpdatePictureModal = false;
		});
	}

	/**
	 * Submit user details modal changes, update the current user and close modal on success
	 */
	public submitUserDetailsModal(): void {
		const updateUser = this.userDetailsDialogComponent.updateUser();
		if (updateUser) {
			this.updateCurrentUser = updateUser.pipe(
				takeUntilDestroyed(this.destroyRef),
				tap((user: UserModel) => {
					this.currentUser = user;
					this.showUserDetailsModal = false;
				}),
			);
		}
	}

	/**
	 * Retrieves paginated list of project times.
	 * @param {number} pageSelected
	 */
	public getProjectTimesPage(pageSelected: number): void {
		this.projectTimesPageNo = pageSelected;
		const params = {
			name: this.projectTimesSearch,
			userId: this.currentUser.id,
		};

		// Get a page of project times.
		this.projectTimeService
			.postPaginate({
				params: params,
				skip: (this.projectTimesPageNo - 1) * this.paginateItemsPerPage,
				limit: this.paginateItemsPerPage,
			})
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(res => {
				this.projectTimesArray = res[0].items;
			});
	}

	/**
	 * Retrieves time entries from the API.
	 * @private
	 */
	private getTimeEntries(): void {
		this.loadingSessions = this.userTimeService
			.getTimeEntries(this.localStorageService.getUserId())
			.pipe(
				finalize(() => {
					this.loadingSessions.unsubscribe();
				}),
			)
			.subscribe(result => {
				this.sessions = result.sessions;
				this.totalUserTime = result.total;
			});
	}

	/**
	 * Uploads on amazon and saves the profile picture of the current user.
	 * @param blob
	 */
	// TODO convert Promises to observables
	private uploadAndSaveImage(blob: File | Blob): Promise<any> {
		return new Promise((resolve, reject) => {
			if (blob === null) {
				// Remove picture.
				this.currentUser.displayPictureKey = null;
				this.userPictureUrl = Constants.PROFILE_DEFAULT_PICTURE;
				this.localStorageService.setUserKey(LOCAL_STORAGE_KEYS.displayPictureKey, this.currentUser.displayPictureKey);
				return this.userService.updatePicture(this.currentUser.id, null).subscribe(resolve, reject);
			}
			// Update picture.
			const path = `user/${this.currentUser.id}/picture_${new Date().toISOString()}_${uuid()}`;
			this.s3Service.uploadFile(blob, path, blob.type).subscribe(returnKey => {
				this.userService.updatePicture(this.currentUser.id, returnKey).subscribe(user => {
					this.currentUser.displayPictureKey = user.displayPictureKey;
					this.localStorageService.setUserKey(LOCAL_STORAGE_KEYS.displayPictureKey, this.currentUser.displayPictureKey);
					this.s3Service.getSignedUrl(this.currentUser.displayPictureKey).subscribe(url => {
						this.userPictureUrl = url;
						resolve(null);
					}, reject);
				}, reject);
			}, reject);
		});
	}
}
